import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Flag, Ban, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const REPORT_CATEGORIES = [
  { value: 'fake_profile', label: 'Fake Profile' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'inappropriate_content', label: 'Inappropriate Content' },
  { value: 'spam', label: 'Spam' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'other', label: 'Other' },
];

export default function BlockReportDialog({ userId, userName, onBlock }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('report');
  const [category, setCategory] = useState('fake_profile');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleBlock = async () => {
    setLoading(true);
    try {
      await api.post(`/block/${userId}`);
      toast.success(`${userName} has been blocked`);
      onBlock?.();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to block user');
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    if (!reason.trim()) { toast.error('Please describe the issue'); return; }
    setLoading(true);
    try {
      await api.post(`/report/${userId}`, { reason: reason.trim(), category, details: '' });
      toast.success('Report submitted. Our team will review it.');
      setOpen(false);
      setReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}
        className="rounded-full px-4 border-red-200 text-red-600 hover:bg-red-50"
        data-testid="block-report-btn">
        <Flag className="w-4 h-4 mr-1" /> {t('profile.block')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2424]">Block or Report {userName}</DialogTitle>
          </DialogHeader>

          <div className="flex gap-2 mb-4">
            <Button size="sm" onClick={() => setMode('report')} variant={mode === 'report' ? 'default' : 'outline'}
              className={`rounded-full ${mode === 'report' ? 'bg-[#D9381E] text-white' : ''}`}>
              <Flag className="w-3.5 h-3.5 mr-1" /> Report
            </Button>
            <Button size="sm" onClick={() => setMode('block')} variant={mode === 'block' ? 'default' : 'outline'}
              className={`rounded-full ${mode === 'block' ? 'bg-red-600 text-white' : ''}`}>
              <Ban className="w-3.5 h-3.5 mr-1" /> Block
            </Button>
          </div>

          {mode === 'report' ? (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Category</Label>
                <RadioGroup value={category} onValueChange={setCategory} className="mt-2 space-y-2">
                  {REPORT_CATEGORIES.map(cat => (
                    <Label key={cat.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      category === cat.value ? 'border-[#D9381E] bg-[#D9381E]/5' : 'border-[#E7E5E4] hover:bg-[#F9F5F0]'
                    }`}>
                      <RadioGroupItem value={cat.value} />
                      <span className="text-sm">{cat.label}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label className="text-sm font-medium">Describe the issue</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Tell us what happened..." rows={3} className="mt-1.5" data-testid="report-reason-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={handleReport} disabled={loading}
                  className="bg-[#D9381E] text-white rounded-full" data-testid="submit-report-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Flag className="w-4 h-4 mr-1" />}
                  Submit Report
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[#78716C]">
                Blocking {userName} will:
              </p>
              <ul className="text-sm text-[#78716C] space-y-1 list-disc pl-5">
                <li>Hide their profile from your search results</li>
                <li>Prevent them from seeing your profile</li>
                <li>Remove any existing chat between you</li>
              </ul>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={handleBlock} disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="confirm-block-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Ban className="w-4 h-4 mr-1" />}
                  Block {userName}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
